// NPM Imports
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { withRouter } from "react-router-dom";
import { Toastr, Tooltip } from "@revvsales/components";

// Components Imports
import DocSaveIndicator from "../DocSaveIndicator";
import { DateSelect } from "../../../commonComponents";
import Breadcrumb from "../../../commonComponents/Breadcrumbs";

// Actions Imports
import { QuotationActions } from "../../../actions";
import {
  updateDocDetails,
  nukeDocument,
} from "../../../actions/DocumentActions";

// Internal Imports
import { GlobalUtils, OppoUtils, AmplitudeUtils } from "../../../utils/index";
import { PATHS, EVENTS } from "../../../constants";
import PDF_ICO from "../../../assets/document-icons/pdf.png";
import DOCUMENT_ICO from "../../../assets/document-icons/doc.png";

// Destructuring
const { toggleTabView } = QuotationActions;
const { decideDeadlineDateClassName, decideDeadlineDaysCount } = OppoUtils;
const {
  getAssetFolderUrl,
  readDataFromLocalStorage,
  removeDataFromLocalStorage,
  truncateString,
} = GlobalUtils;
const { amplitudeLogEvent } = AmplitudeUtils;

// Declarations
const DOCUMENT = "DOCUMENT";

class QuotationTopbarInfo extends Component {
  static oldDocumentName = "";

  state = {
    docName: "",
    turnAroundDate: "",
    docNameBtnLoader: false,
    showDocNameInputBox: false,
    turnAroundDateLoader: false,
    showTurnAroundDateBox: false,
    documentPageSource: "",
  };

  componentDidMount() {
    const { name, deadlineDate } = this.props;
    QuotationTopbarInfo.oldDocumentName = name;
    this.setState(
      {
        docName: name,
        turnAroundDate: moment(deadlineDate).format("YYYY-MM-DD"),
        documentPageSource: readDataFromLocalStorage("documentPageSource"),
      },
      () => removeDataFromLocalStorage("documentPageSource"),
    );
  }

  static getDerivedStateFromProps(nextProps) {
    const { name } = nextProps;
    if (nextProps.name !== QuotationTopbarInfo.oldDocumentName) {
      QuotationTopbarInfo.oldDocumentName = name;
      return { docName: name };
    }
    return null;
  }

  _inlineInputChangeHandler = event => {
    this.setState({
      [event.target.name]: event.target.value,
    });
  };

  _showInlineInputBox = type => {
    this.setState(prevState => ({
      [type]: !prevState[type],
    }));
  };

  _dateChangeHandler = date => {
    this.setState(
      {
        turnAroundDate: moment(date).format("YYYY-MM-DD"),
      },
      () => {
        const { turnAroundDate } = this.state;
        const { deadlineDate } = this.props;
        this._updateDocDetails(
          "deadline_date",
          moment(turnAroundDate),
          "turnAroundDate",
          deadlineDate,
          "showTurnAroundDateBox",
          "turnAroundDateLoader",
        );
      },
    );
  };

  _handleKeyDown = e => {
    if (e.key === "Enter") {
      this._updateDocName();
    }
  };

  _updateDocName = () => {
    const { docName } = this.state;
    const { name } = this.props;

    if (docName) {
      this._updateDocDetails(
        "title",
        docName,
        "docName",
        name,
        "showDocNameInputBox",
        "docNameBtnLoader",
      );
    } else {
      Toastr.showToastr({
        type: "failure",
        message: "Document name can not be empty!...",
        timeout: 6000,
      });
      this.setState({ docName: name, showDocNameInputBox: false });
    }
  };

  _updateDocDetails = (
    key,
    value,
    typeKey,
    typeValue,
    typeFlag,
    typeLoader,
  ) => {
    this.setState({ [typeLoader]: true });
    const payload = {
      data: [
        {
          op: "replace",
          field: key,
          value: {
            [key]: value,
          },
        },
      ],
      docId: this.props.docId,
    };
    this.props
      .updateDocDetails(payload)
      .then(res => {
        if (key === "title") document.title = `Document | ${res.title}`;
        this.setState({ [typeFlag]: false, [typeLoader]: false });
        amplitudeLogEvent(EVENTS.EVENT_NAMES.DOCUMENT_RENAMED, {
          initiatedFrom: "Document Name",
          status: this.props.documentStatus && this.props.documentStatus.name,
          tag: this.props.documentTag && this.props.documentTag.name,
          DocumentID: this.props.docId,
          templateID: this.props.templateID,
        });
      })
      .catch(error => {
        this.setState({
          [typeFlag]: false,
          [typeLoader]: false,
          [typeKey]: typeValue,
        });
        console.error(`QuotationTopbarInfo => _updateDocName => ${error}`);
        Toastr.showToastr({
          type: "failure",
          message: "Something went wrong. Please try again!",
          timeout: 6000,
        });
      });
  };

  _getDocumentName = () => {
    const { showDocNameInputBox, docName } = this.state;
    const { isLocked, isTemplateTypeForm } = this.props;
    if (isLocked) {
      return (
        <Fragment>
          <span
            data-test="quotationtopbarinfo-quotedetails-docname"
            className="quotedetails-docname">
            <Tooltip
              placement="bottom"
              align={{
                offset: [0, -4],
              }}
              overlay={`${docName}`}>
              <div>{docName ? truncateString(docName, 55, true) : null}</div>
            </Tooltip>
          </span>
          {isTemplateTypeForm && (
            <span className="quotationtopbarinfo-form-tag">Form</span>
          )}
        </Fragment>
      );
    }
    return showDocNameInputBox ? (
      <Fragment>
        <input
          className="quotedetails-docname-input"
          type="text"
          value={docName}
          name="docName"
          onChange={this._inlineInputChangeHandler}
          onBlur={this._updateDocName}
          onKeyDown={this._handleKeyDown}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          data-test="quotationtopbarinfo-docname-input"
        />
        {isTemplateTypeForm && (
          <span className="quotationtopbarinfo-form-tag">Form</span>
        )}
      </Fragment>
    ) : (
      <Tooltip
        placement="bottom"
        align={{
          offset: [0, -4],
        }}
        overlay={`${isLocked ? "" : "Edit Document Name"}`}>
        <Fragment>
          <span
            data-test="quotationtopbarinfo-docname"
            className="quotedetails-docname editable"
            onClick={
              isLocked
                ? () => {}
                : () => this._showInlineInputBox("showDocNameInputBox")
            }
            onKeyPress={() => {}}
            role="button"
            tabIndex="0">
            {docName ? truncateString(docName, 55, true) : null}
          </span>
          {isTemplateTypeForm && (
            <span className="quotationtopbarinfo-form-tag">Form</span>
          )}
        </Fragment>
      </Tooltip>
    );
  };

  _getTurnAroundDate = () => {
    const { revisionNo } = this.props;
    const { showTurnAroundDateBox, turnAroundDate } = this.state;

    const dlDate = GlobalUtils.formatDate(turnAroundDate, "lll");
    const currentDate = new Date();
    const deadlineLeftHours = moment(dlDate).diff(
      moment(currentDate),
      "days",
      true,
    );

    // const editTurnAroundDateText = (
    //   <Tooltip placement="bottom" overlay="Edit Turn Around Date">
    //     <span
    //       className="quotedetails-info-details turnaround-date-text edit-turnaround-date"
    //       onClick={() => this._showInlineInputBox("showTurnAroundDateBox")}
    //       onKeyPress={() => {}}
    //       role="button"
    //       tabIndex="0">
    //       Turn Around Date
    //     </span>
    //   </Tooltip>
    // );
    const turnAroundDateText = (
      <span
        data-test="quotationtopbarinfo-turnaround-date-text"
        className="quotedetails-info-details turnaround-date-text">
        Turn around date
      </span>
    );
    let getTurnAroundDate =
      dlDate !== "Invalid date" ? (
        <Tooltip
          placement="bottom"
          align={{
            offset: [0, -4],
          }}
          overlay={decideDeadlineDaysCount(deadlineLeftHours)}>
          <span
            data-test="quotationtopbarinfo-document-deadlinedate"
            className={`document-deadlinedate ${decideDeadlineDateClassName(
              deadlineLeftHours,
            )}`}
            // onClick={
            //   isLocked
            //     ? () => {}
            //     : () => this._showInlineInputBox("showTurnAroundDateBox")
            // }
            // onKeyPress={() => {}}
            // role="button"
            // tabIndex="0"
          >
            {GlobalUtils.formatDate(turnAroundDate, "ll")}
          </span>
        </Tooltip>
      ) : (
        "-"
      );
    if (this.state.turnAroundDateLoader) {
      getTurnAroundDate = (
        <FontAwesomeIcon icon={["far", "spinner"]} className="fa-spin" />
      );
    }
    return (
      <span className="quotedetails-info-wrapper">
        {turnAroundDateText}
        {showTurnAroundDateBox ? (
          <DateSelect
            className="quotedetails-inline-date-box"
            passDefaultDate
            selectedDate={moment(turnAroundDate)}
            returnValue={date => this._dateChangeHandler(date)}
          />
        ) : (
          <span className="turnaround-date">{getTurnAroundDate}</span>
        )}
        {revisionNo > 0 && (
          <span className="quotedetails-revision-wrapper">
            <span
              data-test="quotationtopbarinfo-document-revision-label"
              className="pipelineswimlanecard-document-revision-label">
              Revision {revisionNo}
            </span>
          </span>
        )}
      </span>
    );
  };

  _getDocLockStatus = () => {
    const { isLocked, docStage } = this.props;
    let overlayHeader = "";
    if (docStage === "APPROVED") {
      overlayHeader = "Document is locked as it is already approved";
    } else if (docStage === "DECLINED") {
      overlayHeader = "Document is locked as it has been rejected";
    } else if (docStage === "SENT") {
      overlayHeader =
        "Document is locked as it was sent for acceptance or signature";
    } else if (docStage === "COMPLETED") {
      overlayHeader =
        "Document is locked and sealed as it has been accepted or eSigned";
    }
    let lockedMessage = null;
    if (isLocked) {
      lockedMessage = (
        <span className="quotedetails-info-id">
          <Tooltip placement="right" overlay={overlayHeader}>
            <span className="locked-document">
              <i>
                <FontAwesomeIcon icon={["fas", "lock"]} />
              </i>
            </span>
          </Tooltip>
        </span>
      );
    }
    return lockedMessage;
  };

  render() {
    const {
      isTemplateTypeForm,
      assetBreadcrumbData,
      currentFolderID,
      isPdfDocument,
      isLocked,
    } = this.props;
    let fileCSS = "";
    if (isPdfDocument) {
      fileCSS = "documentdetails-pdf-file";
    } else {
      fileCSS = "documentdetails-ic-file";
    }
    return (
      <div
        data-test="quotationtopbarinfo-documentdetails-wrap"
        className="documentdetails-wrap">
        {/* ---- Go Back Button Section ----- */}
        <div className="doc-goback-btn">
          {/*
            this.state.documentPageSource has a string value of either 'pipeline'/'assets' means user came
            here from one of those pages and not some external link (email)
            */
          typeof this.state.documentPageSource === "string" &&
          this.state.documentPageSource.length > 0 ? (
            <span
              data-test="quotationtopbarinfo-doc-goback-btn"
              role="button"
              className="doc-goback-btnicon"
              tabIndex="0"
              onClick={() => {
                this.props.history.goBack();
                this.props.toggleTabView(DOCUMENT);
                this.props.nukeDocument();
              }}
              onKeyDown={() => null}>
              <FontAwesomeIcon icon={["fad", "home"]} />
            </span>
          ) : (
            <span
              data-test="quotationtopbarinfo-doc-goback-btn"
              role="button"
              className="doc-goback-btnicon"
              tabIndex="0"
              onClick={() => {
                this.props.history.push(PATHS.APP_MAIN_PAGE);
                this.props.toggleTabView(DOCUMENT);
                this.props.nukeDocument();
              }}
              onKeyDown={() => null}>
              <FontAwesomeIcon icon={["fad", "home"]} />
            </span>
          )}
        </div>

        {/* ---- Document Details Info Section ----- */}
        <div className="documentdetails-info">
          <span className={`${fileCSS} ${isTemplateTypeForm ? "is-form" : ""}`}>
            {isPdfDocument ? (
              <img src={PDF_ICO} alt="pdf-icon" />
            ) : (
              <img src={DOCUMENT_ICO} alt="doc-icon" />
            )}
          </span>
          <div className="quotedetails-title quotedetails-title-wrapper">
            {/* ---- Document Name Section ----- */}
            <span className="quotedetails-info-id quotedetails-input-wrapper">
              {this._getDocumentName()}
              {this._getDocLockStatus()}
              {!isLocked && <DocSaveIndicator />}
            </span>

            {/* ---- Turn Around Date Section ----- */}
            <span className="quotedetails-turnarounddate-wrapper">
              {/* {this._getTurnAroundDate()}
              <span className="circle-dot" /> */}
              {/* breadcrumbs */}
              <Breadcrumb
                breadcrumbData={assetBreadcrumbData.map(item => ({
                  ...item,
                  url: getAssetFolderUrl(item.name, item.id),
                }))}
                currentFolderID={currentFolderID}
              />
            </span>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const {
    document: {
      id: docId,
      name,
      revisionNo,
      deadline_date: deadlineDate,
      status: docStatus,
      scope,
      template_id: templateID,
      stage: docStage,
      isPdfDocument,
    },
    showDocumentDiffView,
  } = state.DocumentStore;
  const { assetBreadcrumbData, currentFolderID } = state.AssetStore;
  return {
    docId,
    name,
    templateID,
    scope,
    docStatus,
    revisionNo,
    deadlineDate,
    showDocumentDiffView,
    assetBreadcrumbData,
    currentFolderID,
    docStage,
    isPdfDocument,
  };
};

const mapDispatchToProps = {
  toggleTabView,
  updateDocDetails,
  nukeDocument,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(QuotationTopbarInfo));



// WEBPACK FOOTER //
// ./src/components/QuotationTopbar/QuotationTopbarInfo/QuotationTopbarInfo.js